"use client";

import { useState, useEffect, useMemo, useCallback } from "react";
import CdnImage from "@/components/CdnImage";
import Link from "next/link";
import { marketPageUrl } from "@/lib/source-url";
import { Search, ArrowUpDown, ChevronDown, MessageSquare, ThumbsUp, ExternalLink, BarChart3, Clock } from "lucide-react";
import dynamic from "next/dynamic";
import { useT } from "@/hooks/useT";

const WalletVolumeChart = dynamic(() => import("./WalletVolumeChart"), { ssr: false });

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Position {
  proxyWallet: string;
  asset: string;
  conditionId: string;
  size: number;
  avgPrice: number;
  curPrice: number;
  initialValue: number;
  currentValue: number;
  cashPnl: number;
  percentPnl: number;
  realizedPnl: number;
  percentRealizedPnl: number;
  totalBought: number;
  redeemable: boolean;
  mergeable: boolean;
  title: string;
  slug: string;
  icon: string;
  eventSlug: string;
  isMultiOutcome?: boolean;
  outcome: string;
  outcomeIndex: number;
  oppositeOutcome: string;
  endDate: string | null;
  negativeRisk: boolean;
}

interface ClosedPosition {
  proxyWallet: string;
  asset: string;
  conditionId: string;
  avgPrice: number;
  totalBought: number;
  realizedPnl: number;
  curPrice: number;
  title: string;
  slug: string;
  icon: string;
  eventSlug: string;
  isMultiOutcome?: boolean;
  outcome: string;
  outcomeIndex: number;
  oppositeOutcome: string;
  endDate: string | null;
  timestamp: number;
}

interface ActivityItem {
  proxyWallet: string;
  timestamp: number;
  conditionId: string;
  type: "TRADE" | "REWARD" | "REFERRAL_REWARD" | "YIELD" | "REDEEM" | "SPLIT" | "MERGE" | "CONVERSION" | "MAKER_REBATE";
  size: number;
  usdcSize: number;
  price: number;
  side: "BUY" | "SELL";
  asset: string;
  outcome: string;
  outcomeIndex: number;
  title: string;
  slug: string;
  icon: string;
  eventSlug: string;
  isMultiOutcome?: boolean;
  transactionHash: string;
  name: string;
  pseudonym: string;
  profileImage: string;
}

interface TraderComment {
  id: string;
  body: string;
  createdAt: string;
  reactionCount: number;
  eventTitle: string | null;
  eventSlug: string | null;
  eventImage: string | null;
}

interface PredictPosition {
  marketId?: number;
  market?: string;
  question?: string;
  imageUrl?: string;
  categorySlug?: string;
  status?: string;
  tradingStatus?: string;
  outcome?: string;
  outcomeStatus?: string;
  valueUsd?: number;
  pnlUsd?: number;
  avgBuyPrice?: number;
  shares?: string;
}

interface TraderPortfolioProps {
  wallet: string;
  username?: string;
  avatar?: string;
  source?: string;
  predictPositions?: PredictPosition[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatUsd(value: number): string {
  if (!Number.isFinite(value)) return "$0.00"; // null/undefined/NaN-safe
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 10_000) return `$${(abs / 1_000).toFixed(1)}K`;
  if (abs >= 1_000) return `$${(abs / 1_000).toFixed(2)}K`;
  if (abs >= 1) return `$${abs.toFixed(2)}`;
  return `$${abs.toFixed(3)}`;
}

function formatPrice(price: number): string {
  if (!Number.isFinite(price)) return "—";
  // 1 decimal of a cent, but drop a trailing ".0" so whole cents stay clean
  // (0.6979 → "69.8¢", 0.27 → "27¢"). Whole-cent rounding via toFixed(0) made
  // real sub-cent entries look rounded — a 62.82¢ entry showed as 63¢.
  const cents = (price * 100).toFixed(1).replace(/\.0$/, "");
  return `${cents}¢`;
}

function formatShares(size: number): string {
  if (!Number.isFinite(size)) return "0";
  if (size >= 1_000_000) return `${(size / 1_000_000).toFixed(1)}M`;
  if (size >= 1_000) return `${(size / 1_000).toFixed(1)}K`;
  return size.toFixed(1);
}

function timeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return "now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// "Active" = open market only, mirroring what polymarket.com lists as a
// trader's current positions. Three exclusions, each verified against PM's
// data-api for a real wallet (Mine-Carrying-Dolphin, 2026-06-01: we showed 21,
// PM showed 6):
//   1. curPrice 0  → resolved / no live price.
//   2. redeemable / resolved (status !== 'open') → settled markets. Predexon's
//      wallet/positions returns resolved_win/resolved_loss leftovers (e.g. 0.01
//      shares at $1.00) that PM drops from current positions. The Predexon
//      mapping below now sets `resolved`/`redeemable` from `status`; before, it
//      hardcoded redeemable:false and never set resolved, so these slipped
//      through (curPrice 1.0 > 0).
//   3. size < 1 share → dust. PM's positions view uses sizeThreshold=1; a
//      sub-1-share position is by definition worth < $1 (value = size×price,
//      price ≤ 1). Matches PM exactly (keeps a 300-share $0.30 position, drops
//      a 0.02-share one).
function isOpenPosition(p: Position): boolean {
  return p.curPrice > 0
    && !p.redeemable
    && !(p as { resolved?: boolean }).resolved
    && p.size >= 1;
}

function pnlColor(value: number): string {
  if (value > 0) return "text-emerald-500";
  if (value < 0) return "text-red-400";
  return "text-gray-400";
}

function pnlSign(value: number): string {
  if (value > 0) return "+";
  if (value < 0) return "-";
  return "";
}

// ---------------------------------------------------------------------------
// Sorting
// ---------------------------------------------------------------------------

type SortKey = "title" | "avgPrice" | "curPrice" | "size" | "value" | "pnl";
type ClosedSortKey = "title" | "cost" | "amountWon" | "pnl";
type SortDir = "asc" | "desc";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function TraderPortfolio({ wallet, username, avatar, source = 'polymarket', predictPositions }: TraderPortfolioProps) {
  const t = useT();
  const isPredict = source === 'predict';
  // Deep-linkable tab via URL hash, so /traders/@x#volume lands straight on
  // that tab. Hash (not query) keeps the URL clean. Initial read of
  // window.location.hash runs in a lazy initializer so SSR stays stable and
  // the first render already shows the right tab — no flash of "Positions".
  const VALID_TABS = ["positions", "activity", "comments", "volume"] as const;
  type TabKey = (typeof VALID_TABS)[number];
  const [activeTab, setActiveTab] = useState<TabKey>(() => {
    if (typeof window === "undefined") return "positions";
    const hash = window.location.hash.replace(/^#/, "").toLowerCase();
    return (VALID_TABS as readonly string[]).includes(hash) ? (hash as TabKey) : "positions";
  });

  // Keep the URL hash in sync with the active tab, and react to back/forward
  // navigation that mutates the hash (browser nav, manual edit).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const desired = `#${activeTab}`;
    if (window.location.hash !== desired) {
      // replaceState — don't create a history entry per click, so the back
      // button still returns to the previous page instead of stepping
      // through each tab the user touched.
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${desired}`);
    }
  }, [activeTab]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onHashChange = () => {
      const hash = window.location.hash.replace(/^#/, "").toLowerCase();
      if ((VALID_TABS as readonly string[]).includes(hash) && hash !== activeTab) {
        setActiveTab(hash as TabKey);
      }
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [positionFilter, setPositionFilter] = useState<"active" | "closed" | "all" | "open" | "resolved">(isPredict ? "all" : "active");
  const [searchQuery, setSearchQuery] = useState("");
  const [positions, setPositions] = useState<Position[]>([]);
  const [closedPositions, setClosedPositions] = useState<ClosedPosition[]>([]);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [loadingPositions, setLoadingPositions] = useState(true);
  const [loadingClosed, setLoadingClosed] = useState(false);
  const [closedLoaded, setClosedLoaded] = useState(false);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [activityLoaded, setActivityLoaded] = useState(false);
  const [comments, setComments] = useState<TraderComment[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [visibleComments, setVisibleComments] = useState(20);
  // Trade modal state removed — outcome clicks now route the user
  // straight to /<source>/event/<slug>?market=&outcome= (the row's
  // existing <Link> already does this). Keeping a competing modal on
  // a trader profile was redundant with the event page's own buy flow
  // and noisier on hover.
  const [visiblePositions, setVisiblePositions] = useState(6);
  const [visibleActivity, setVisibleActivity] = useState(30);
  const [sortKey, setSortKey] = useState<SortKey>("pnl");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [closedSortKey, setClosedSortKey] = useState<ClosedSortKey>("pnl");
  const [closedSortDir, setClosedSortDir] = useState<SortDir>("desc");

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  };
  const toggleClosedSort = (key: ClosedSortKey) => {
    if (closedSortKey === key) setClosedSortDir(d => d === "asc" ? "desc" : "asc");
    else { setClosedSortKey(key); setClosedSortDir("desc"); }
  };

  // Fetch active positions on mount — try Predexon first (includes cost basis + PnL), fallback to PM
  useEffect(() => {
    // Predict.fun: use pre-fetched positions from backend (REST API + GraphQL indexed)
    if (isPredict && predictPositions && predictPositions.length > 0) {
      const parseShares = (raw: string | undefined): number => {
        if (!raw) return 0;
        try {
          // Wei string (18 decimals) → human readable
          const n = BigInt(raw);
          return Number(n / BigInt('1000000000000000000'));
        } catch {
          return Number(raw) || 0;
        }
      };

      const mapped = predictPositions.map((p, i) => {
        const shares = parseShares(p.shares);
        const value = p.valueUsd || 0;
        const avgPrice = p.avgBuyPrice || (shares > 0 ? value / shares : 0);
        const curPrice = shares > 0 ? value / shares : 0;

        return {
          proxyWallet: wallet,
          asset: String(p.marketId || i),
          conditionId: '',
          size: shares,
          avgPrice,
          curPrice,
          initialValue: shares * avgPrice,
          currentValue: value,
          cashPnl: p.pnlUsd || 0,
          percentPnl: avgPrice > 0 ? ((curPrice - avgPrice) / avgPrice) * 100 : 0,
          realizedPnl: 0,
          percentRealizedPnl: 0,
          totalBought: shares * avgPrice,
          redeemable: p.outcomeStatus === 'WON',
          mergeable: false,
          resolved: p.status === 'RESOLVED' || p.tradingStatus === 'CLOSED',
          title: p.market || p.question || '',
          slug: p.categorySlug || '',
          icon: p.imageUrl || '',
          eventSlug: p.categorySlug || '',
          outcome: p.outcome || 'Yes',
          outcomeIndex: p.outcome === 'No' ? 1 : 0,
          oppositeOutcome: p.outcome === 'No' ? 'Yes' : 'No',
          endDate: null,
          negativeRisk: false,
          eventImage: p.imageUrl || null,
        };
      });
      setPositions(mapped);
      setLoadingPositions(false);
      return;
    }

    const fetchPositions = async () => {
      setLoadingPositions(true);
      try {
        // Try Predexon positions first (richer: entry price, unrealized PnL)
        const predexonRes = await fetch(`/api/analytics/wallet/${wallet}/positions`);
        if (predexonRes.ok) {
          const predexonData = await predexonRes.json();
          if (predexonData?.success && predexonData.data?.length > 0) {
            const mapped = predexonData.data
              .filter((p: any) => (p.shares || p.size || 0) > 0.01)
              .map((p: any) => {
              // Predexon ships a `status` ('open' | 'resolved_win' |
              // 'resolved_loss' | …). Anything that isn't open is a settled
              // market — flag it so isOpenPosition drops it from the active
              // list (PM doesn't list resolved holdings as current positions).
              const status = String(p.status || '').toLowerCase();
              const resolved = status !== '' && status !== 'open' && status !== 'active' && status !== 'live';
              return {
                proxyWallet: wallet,
                asset: p.token_id || '',
                conditionId: p.condition_id || '',
                size: p.shares || p.size || 0,
                avgPrice: p.entry_price || p.avgPrice || 0,
                curPrice: p.current_price || p.curPrice || 0,
                initialValue: (p.shares || 0) * (p.entry_price || 0),
                currentValue: p.current_value || (p.shares || 0) * (p.current_price || 0),
                cashPnl: p.unrealized_pnl || p.cashPnl || 0,
                percentPnl: p.entry_price ? ((p.current_price || 0) - p.entry_price) / p.entry_price * 100 : 0,
                realizedPnl: p.realized_pnl || 0,
                percentRealizedPnl: 0,
                totalBought: (p.shares || 0) * (p.entry_price || 0),
                resolved,
                redeemable: status === 'resolved_win',
                mergeable: false,
                title: p.title || p.market_slug || '',
                slug: p.market_slug || '',
                // Predexon doesn't ship a market thumbnail; the backend
                // analytics route now enriches each position with `icon`
                // and `image` from gamma-api. Accept either so we render
                // the thumbnail regardless of which field comes back.
                icon: p.icon || p.image || '',
                eventImage: p.image || p.icon || null,
                eventSlug: p.event_slug || '',
                outcome: p.outcome || (p.outcome_index === 0 ? 'Yes' : 'No'),
                outcomeIndex: p.outcome_index ?? 0,
                oppositeOutcome: p.outcome_index === 0 ? 'No' : 'Yes',
                endDate: p.end_date || null,
                negativeRisk: false,
              };
            });
            setPositions(mapped);
            setLoadingPositions(false);
            return;
          }
        }

        // Fallback to original endpoint. Normalize defensively: the raw shape
        // can arrive with missing/alt-named numeric fields (currentValue,
        // cashPnl, percentPnl), which rendered as NaN before. Derive anything
        // missing from size/price so the columns stay numeric.
        const res = await fetch(`/api/traders/${wallet}/positions?limit=200&sizeThreshold=0.01`);
        if (res.ok) {
          const data = await res.json();
          const mapped = (data.positions || [])
            .filter((p: any) => Number(p.size ?? p.shares ?? 0) > 0.01)
            .map((p: any) => {
              const size = Number(p.size ?? p.shares ?? 0) || 0;
              const avgPrice = Number(p.avgPrice ?? p.entry_price ?? 0) || 0;
              const curPrice = Number(p.curPrice ?? p.current_price ?? 0) || 0;
              const currentValue = Number(p.currentValue ?? p.current_value ?? size * curPrice) || 0;
              const cashPnl = Number(p.cashPnl ?? p.unrealized_pnl ?? currentValue - size * avgPrice) || 0;
              return {
                ...p,
                size,
                avgPrice,
                curPrice,
                currentValue,
                cashPnl,
                initialValue: Number(p.initialValue ?? size * avgPrice) || 0,
                percentPnl: Number(p.percentPnl) || (avgPrice > 0 ? ((curPrice - avgPrice) / avgPrice) * 100 : 0),
                realizedPnl: Number(p.realizedPnl ?? p.realized_pnl ?? 0) || 0,
                totalBought: Number(p.totalBought ?? size * avgPrice) || 0,
                title: p.title || p.slug || '',
                outcome: p.outcome || (p.outcomeIndex === 0 ? 'Yes' : 'No'),
              };
            });
          setPositions(mapped);
        }
      } catch {
        // silent
      } finally {
        setLoadingPositions(false);
      }
    };
    fetchPositions();
  }, [wallet, isPredict, predictPositions]);

  // Fetch closed positions when tab switches
  const loadClosedPositions = useCallback(async () => {
    if (closedLoaded) return;
    setLoadingClosed(true);
    try {
      const res = await fetch(`/api/traders/${wallet}/closed-positions?limit=200`);
      if (res.ok) {
        const data = await res.json();
        setClosedPositions(data.positions || []);
      }
    } catch {
      // silent
    } finally {
      setLoadingClosed(false);
      setClosedLoaded(true);
    }
  }, [wallet, closedLoaded]);

  useEffect(() => {
    if (positionFilter === "closed") {
      loadClosedPositions();
    }
  }, [positionFilter, loadClosedPositions]);

  // Fetch activity when tab switches
  const loadActivity = useCallback(async () => {
    if (activityLoaded) return;
    setLoadingActivity(true);
    try {
      const res = await fetch(`/api/traders/${wallet}/activity?limit=100`);
      if (res.ok) {
        const data = await res.json();
        setActivity(data.activity || []);
      }
    } catch {
      // silent
    } finally {
      setLoadingActivity(false);
      setActivityLoaded(true);
    }
  }, [wallet, activityLoaded]);

  useEffect(() => {
    if (activeTab === "activity") {
      loadActivity();
    }
  }, [activeTab, loadActivity]);

  // Fetch comments when tab switches
  const loadComments = useCallback(async () => {
    if (commentsLoaded) return;
    setLoadingComments(true);
    try {
      const res = await fetch(`/api/traders/${wallet}/comments?limit=50`);
      if (res.ok) {
        const data = await res.json();
        setComments(data.comments || []);
      }
    } catch {
      // silent
    } finally {
      setLoadingComments(false);
      setCommentsLoaded(true);
    }
  }, [wallet, commentsLoaded]);

  useEffect(() => {
    if (activeTab === "comments") {
      loadComments();
    }
  }, [activeTab, loadComments]);

  // Filter + sort active positions
  const filteredPositions = useMemo(() => {
    let filtered = positions.filter(isOpenPosition);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((p) => p.title?.toLowerCase().includes(q));
    }
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      switch (sortKey) {
        case "title": return dir * (a.title || "").localeCompare(b.title || "");
        case "avgPrice": return dir * (a.avgPrice - b.avgPrice);
        case "curPrice": return dir * (a.curPrice - b.curPrice);
        case "size": return dir * (a.size - b.size);
        case "value": return dir * (a.currentValue - b.currentValue);
        case "pnl": return dir * (a.cashPnl - b.cashPnl);
        default: return 0;
      }
    });
  }, [positions, searchQuery, sortKey, sortDir]);

  // Filter + sort closed positions
  const filteredClosedPositions = useMemo(() => {
    let filtered = closedPositions;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((p) => p.title?.toLowerCase().includes(q));
    }
    const dir = closedSortDir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const costA = a.totalBought * a.avgPrice, costB = b.totalBought * b.avgPrice;
      switch (closedSortKey) {
        case "title": return dir * (a.title || "").localeCompare(b.title || "");
        case "cost": return dir * (costA - costB);
        case "amountWon": return dir * ((costA + a.realizedPnl) - (costB + b.realizedPnl));
        case "pnl": return dir * (a.realizedPnl - b.realizedPnl);
        default: return 0;
      }
    });
  }, [closedPositions, searchQuery, closedSortKey, closedSortDir]);

  // Portfolio value for active positions
  const portfolioValue = useMemo(() => {
    return positions.reduce((sum, p) => sum + (p.currentValue || 0), 0);
  }, [positions]);

  const activeCount = positions.filter(isOpenPosition).length;
  const closedCount = closedPositions.length;

  return (
    <>
      <section>
        <div className="bg-white rounded-[5px] md:rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] dark:bg-[#18181D] shadow-lg md:shadow-xl transition-colors duration-300">

          {/* Row 1: Top-level tabs — Positions / Activity */}
          <div className="px-4 md:px-6 pt-4 md:pt-5 pb-3 border-b border-gray-100 dark:border-[#1A1A1A]">
            <div className="flex gap-6 overflow-x-auto scrollbar-hide whitespace-nowrap">
              <button
                onClick={() => { setActiveTab("positions"); setSearchQuery(""); setVisiblePositions(20); }}
                className={`pb-2 text-sm font-bold transition-colors border-b-2 flex items-center gap-1.5 ${
                  activeTab === "positions"
                    ? "text-gray-900 dark:text-white border-gray-900 dark:border-white"
                    : "text-gray-400 border-transparent hover:text-gray-600 dark:hover:text-gray-300"
                }`}
              >
                <BarChart3 className="w-3.5 h-3.5" />
                {t("traders.port.tab.positions")}
              </button>
              <button
                onClick={() => { setActiveTab("activity"); setSearchQuery(""); setVisibleActivity(30); }}
                className={`pb-2 text-sm font-bold transition-colors border-b-2 flex items-center gap-1.5 ${
                  activeTab === "activity"
                    ? "text-gray-900 dark:text-white border-gray-900 dark:border-white"
                    : "text-gray-400 border-transparent hover:text-gray-600 dark:hover:text-gray-300"
                }`}
              >
                <Clock className="w-3.5 h-3.5" />
                {t("traders.port.tab.activity")}
              </button>
              {/* 2026-05-26 (Pika): Comments tab hidden — was a Polymarket-
                  scraped read-only feed that hasn't proved useful in the
                  trader profile context. The fetch / load-more / state for
                  comments is left in place so a follow-up PR can reintroduce
                  the tab if we ever want it back. */}
              {!isPredict && (
                <button
                  onClick={() => setActiveTab("volume")}
                  className={`pb-2 text-sm font-bold transition-colors border-b-2 flex items-center gap-1.5 ${
                    activeTab === "volume"
                      ? "text-gray-900 dark:text-white border-gray-900 dark:border-white"
                      : "text-gray-400 border-transparent hover:text-gray-600 dark:hover:text-gray-300"
                  }`}
                >
                  <BarChart3 className="w-3.5 h-3.5" />
                  {t("traders.port.tab.volume")}
                </button>
              )}
            </div>
          </div>

          {/* Row 2: Sub-filters + Search (only on Positions tab) */}
          {activeTab === "positions" && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 md:px-6 py-3 border-b border-gray-100 dark:border-[#1A1A1A]">
              <div className="flex gap-1 bg-gray-100 dark:bg-[#0f0f12] rounded-[5px] p-0.5">
                {isPredict ? (
                  <>
                    {(["all", "open", "resolved"] as const).map((f) => {
                      const count = f === "all" ? positions.length
                        : f === "open" ? positions.filter((p: any) => !p.resolved).length
                        : positions.filter((p: any) => p.resolved).length;
                      return (
                        <button
                          key={f}
                          onClick={() => setPositionFilter(f)}
                          className={`px-3 py-1.5 text-xs font-semibold rounded-[5px] transition-colors ${
                            positionFilter === f
                              ? "bg-white dark:bg-[#18181D] text-gray-900 dark:text-white shadow-sm"
                              : "text-gray-500 dark:text-gray-400 hover:text-gray-700"
                          }`}
                        >
                          {f === "all" ? t("traders.port.filter.all") : f === "open" ? t("traders.port.filter.open") : t("traders.port.filter.resolved")}{count > 0 ? ` (${count})` : ""}
                        </button>
                      );
                    })}
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => setPositionFilter("active")}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-[5px] transition-colors ${
                        positionFilter === "active"
                          ? "bg-white dark:bg-[#18181D] text-gray-900 dark:text-white shadow-sm"
                          : "text-gray-500 dark:text-gray-400 hover:text-gray-700"
                      }`}
                    >
                      {t("traders.port.filter.active")}{activeCount > 0 ? ` (${activeCount})` : ""}
                    </button>
                    <button
                      onClick={() => setPositionFilter("closed")}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-[5px] transition-colors ${
                        positionFilter === "closed"
                          ? "bg-white dark:bg-[#18181D] text-gray-900 dark:text-white shadow-sm"
                          : "text-gray-500 dark:text-gray-400 hover:text-gray-700"
                      }`}
                    >
                      {t("traders.port.filter.closed")}{closedCount > 0 ? ` (${closedCount})` : ""}
                    </button>
                  </>
                )}
              </div>

              <div className="relative sm:ml-auto sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("traders.port.searchPlaceholder")}
                  className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 dark:bg-[#0f0f12] border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-gray-600"
                />
              </div>
            </div>
          )}

          {/* Portfolio value summary */}
          {activeTab === "positions" && (positionFilter === "active" || positionFilter === "all") && portfolioValue > 0 && (
            <div className="px-4 md:px-6 pt-3">
              <span className="text-xs text-gray-400">{t("traders.port.portfolioValue")}</span>
              <span className="ml-2 text-sm font-bold text-gray-900 dark:text-white">{formatUsd(portfolioValue)}</span>
            </div>
          )}

          <div className="p-4 md:p-6">
            {activeTab === "positions" ? (
              isPredict ? (
                /* Predict: filter from single positions array by status */
                <PositionsTable
                  positions={positionFilter === "all" ? filteredPositions
                    : positionFilter === "open" ? filteredPositions.filter((p: any) => !p.resolved)
                    : filteredPositions.filter((p: any) => p.resolved)}
                  source={source}
                  loading={loadingPositions}
                  visible={visiblePositions}
                  onShowMore={() => setVisiblePositions((v) => v + 20)}

                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSort={toggleSort}
                />
              ) : positionFilter === "active" ? (
                <PositionsTable
                  positions={filteredPositions}
                  source={source}
                  loading={loadingPositions}
                  visible={visiblePositions}
                  onShowMore={() => setVisiblePositions((v) => v + 20)}

                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSort={toggleSort}
                />
              ) : (
                <ClosedPositionsTable
                  positions={filteredClosedPositions}
                  loading={loadingClosed}
                  visible={visiblePositions}
                  onShowMore={() => setVisiblePositions((v) => v + 20)}
                  sortKey={closedSortKey}
                  sortDir={closedSortDir}
                  onSort={toggleClosedSort}
                />
              )
            ) : activeTab === "activity" ? (
              <ActivityTimeline
                activity={activity}
                loading={loadingActivity}
                visible={visibleActivity}
                onShowMore={() => setVisibleActivity((v) => v + 30)}
              />
            ) : activeTab === "volume" ? (
              <WalletVolumeChart wallet={wallet} />
            ) : (
              <CommentsTimeline
                comments={comments}
                loading={loadingComments}
                visible={visibleComments}
                onShowMore={() => setVisibleComments((v) => v + 20)}
              />
            )}
          </div>
        </div>
      </section>
    </>
  );
}

// ---------------------------------------------------------------------------
// Positions Table
// ---------------------------------------------------------------------------

function SortIcon({ active, dir }: { active: boolean; dir: SortDir }) {
  return (
    <ArrowUpDown className={`inline w-3 h-3 ml-0.5 ${active ? "text-gray-900 dark:text-white" : "text-gray-300 dark:text-gray-600"}`} />
  );
}

function PositionsTable({
  positions,
  source = 'polymarket',
  loading,
  visible,
  onShowMore,
  sortKey,
  sortDir,
  onSort,
}: {
  positions: Position[];
  source?: string;
  loading: boolean;
  visible: number;
  onShowMore: () => void;
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
}) {
  const t = useT();
  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="animate-pulse flex items-center gap-3 p-3 rounded-[5px] bg-gray-50 dark:bg-[#0f0f12]">
            <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-[5px]" />
            <div className="flex-1">
              <div className="h-4 w-3/4 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
              <div className="h-3 w-1/3 bg-gray-200 dark:bg-gray-700 rounded" />
            </div>
            <div className="h-5 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (positions.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-gray-400">{t("traders.port.empty.positions")}</p>
      </div>
    );
  }

  const shown = positions.slice(0, visible);

  return (
    <>
      {/* Header row — desktop only.
          2026-05-20: whitespace-nowrap on each header so the column
          name + sort arrow never wrap to a second line. The narrow
          col-span-1 cells (Avg, Current, Value) were breaking
          "Current ↑" into two rows. */}
      <div className="hidden md:grid grid-cols-12 gap-3 text-[10px] uppercase tracking-wider text-gray-400 font-semibold pb-2 mb-1 border-b border-gray-100 dark:border-[#1A1A1A]">
        <button onClick={() => onSort("title")} className="col-span-5 text-left whitespace-nowrap hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
          {t("traders.port.col.market")} <SortIcon active={sortKey === "title"} dir={sortDir} />
        </button>
        <button onClick={() => onSort("avgPrice")} className="col-span-1 text-right whitespace-nowrap hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
          {t("traders.port.col.avg")} <SortIcon active={sortKey === "avgPrice"} dir={sortDir} />
        </button>
        <button onClick={() => onSort("curPrice")} className="col-span-1 text-right whitespace-nowrap hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
          {t("traders.port.col.current")} <SortIcon active={sortKey === "curPrice"} dir={sortDir} />
        </button>
        <button onClick={() => onSort("size")} className="col-span-2 text-right whitespace-nowrap hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
          {t("traders.port.col.shares")} <SortIcon active={sortKey === "size"} dir={sortDir} />
        </button>
        <button onClick={() => onSort("value")} className="col-span-1 text-right whitespace-nowrap hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
          {t("traders.port.col.value")} <SortIcon active={sortKey === "value"} dir={sortDir} />
        </button>
        <button onClick={() => onSort("pnl")} className="col-span-2 text-right whitespace-nowrap hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
          {t("traders.port.col.pnl")} <SortIcon active={sortKey === "pnl"} dir={sortDir} />
        </button>
      </div>

      <div className="space-y-1">
        {shown.map((pos, i) => (
          <PositionRow key={`${pos.asset}-${i}`} position={pos} source={source} />
        ))}
      </div>

      {visible < positions.length && (
        <button
          onClick={onShowMore}
          className="w-full mt-4 py-2.5 text-sm font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 flex items-center justify-center gap-1 transition-colors"
        >
          <ChevronDown className="w-4 h-4" />
          {t("traders.port.showMore", { n: positions.length - visible })}
        </button>
      )}
    </>
  );
}

function PositionRow({
  position: pos,
  source = 'polymarket',
}: {
  position: Position;
  source?: string;
}) {
  const t = useT();
  const pnl = pos.cashPnl;
  const pnlPct = pos.percentPnl;
  const isYes = pos.outcome?.toLowerCase() === "yes" || pos.outcomeIndex === 0;

  return (
    <div className="group flex items-center gap-2 md:grid md:grid-cols-12 md:gap-3 p-2.5 md:p-3 rounded-[5px] hover:bg-gray-50 dark:hover:bg-[#0f0f12] transition-colors">
      {/* Market icon + info */}
      <div className="md:col-span-5 flex items-center gap-2.5 min-w-0 flex-1">
        {pos.icon ? (
          <CdnImage
            src={pos.icon}
            alt={pos.title}
            width={40}
            height={40}
            className="w-9 h-9 md:w-10 md:h-10 rounded-[5px] object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-9 h-9 md:w-10 md:h-10 rounded-[5px] bg-gray-200 dark:bg-gray-700 flex-shrink-0" />
        )}
        <div className="min-w-0">
          {pos.slug ? (
            <Link
              href={source === 'predict' ? `/predictfun/event/${pos.slug}` : marketPageUrl({ marketSlug: pos.slug, eventSlug: pos.eventSlug, outcome: pos.outcome, source: 'polymarket', isMultiOutcome: pos.isMultiOutcome })}
              className="text-xs md:text-sm font-medium text-gray-900 dark:text-white truncate block hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              {pos.title}
            </Link>
          ) : (
            <p className="text-xs md:text-sm font-medium text-gray-900 dark:text-white truncate">
              {pos.title}
            </p>
          )}
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className={`text-[10px] md:text-xs font-bold ${isYes ? "text-emerald-500" : "text-red-400"}`}>
              {pos.outcome} {formatPrice(pos.avgPrice)}
            </span>
            <span className="text-[10px] text-gray-400">
              {formatShares(pos.size)} {t("traders.port.shares")}
            </span>
          </div>
        </div>
      </div>

      {/* Avg price — desktop */}
      <div className="hidden md:block md:col-span-1 text-right">
        <span className="text-xs text-gray-500 dark:text-gray-400">{formatPrice(pos.avgPrice)}</span>
      </div>

      {/* Current price — desktop */}
      <div className="hidden md:block md:col-span-1 text-right">
        <span className="text-xs text-gray-500 dark:text-gray-400">{formatPrice(pos.curPrice)}</span>
      </div>

      {/* Shares — desktop */}
      <div className="hidden md:block md:col-span-2 text-right">
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {formatShares(pos.size)}
        </span>
      </div>

      {/* Value — desktop */}
      <div className="hidden md:block md:col-span-1 text-right">
        <span className="text-xs font-medium text-gray-900 dark:text-white">
          {formatUsd(pos.currentValue)}
        </span>
      </div>

      {/* PnL */}
      <div className="md:col-span-2 text-right flex-shrink-0">
        <div className={`text-xs md:text-sm font-bold ${pnlColor(pnl)}`}>
          {pnlSign(pnl)}{formatUsd(pnl)}
        </div>
        {pnlPct !== 0 && (
          <div className={`text-[10px] ${pnlColor(pnlPct)}`}>
            {pnlSign(pnlPct)}{Math.abs(pnlPct).toFixed(1)}%
          </div>
        )}
      </div>

      {/* Hover YES/NO trade buttons removed — clicking the row's
          title link routes to the event page where the buy flow lives
          (sticky CTA on mobile, sidebar TradingPanel on desktop). */}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Closed Positions Table (uses /closed-positions API — aggregated, clean data)
// ---------------------------------------------------------------------------

function ClosedPositionsTable({
  positions,
  loading,
  visible,
  onShowMore,
  sortKey,
  sortDir,
  onSort,
}: {
  positions: ClosedPosition[];
  loading: boolean;
  visible: number;
  onShowMore: () => void;
  sortKey: ClosedSortKey;
  sortDir: SortDir;
  onSort: (key: ClosedSortKey) => void;
}) {
  const t = useT();
  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="animate-pulse flex items-center gap-3 p-3 rounded-[5px] bg-gray-50 dark:bg-[#0f0f12]">
            <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-[5px]" />
            <div className="flex-1">
              <div className="h-4 w-3/4 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
              <div className="h-3 w-1/3 bg-gray-200 dark:bg-gray-700 rounded" />
            </div>
            <div className="h-5 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (positions.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-gray-400">{t("traders.port.empty.closed")}</p>
      </div>
    );
  }

  const shown = positions.slice(0, visible);

  return (
    <>
      {/* Header row — desktop only */}
      <div className="hidden md:grid grid-cols-12 gap-3 text-[10px] uppercase tracking-wider text-gray-400 font-semibold pb-2 mb-1 border-b border-gray-100 dark:border-[#1A1A1A]">
        <div className="col-span-1">{t("traders.port.col.result")}</div>
        <button onClick={() => onSort("title")} className="col-span-5 text-left hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
          {t("traders.port.col.market")} <SortIcon active={sortKey === "title"} dir={sortDir} />
        </button>
        <button onClick={() => onSort("cost")} className="col-span-2 text-right hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
          {t("traders.port.col.totalTraded")} <SortIcon active={sortKey === "cost"} dir={sortDir} />
        </button>
        <button onClick={() => onSort("amountWon")} className="col-span-4 text-right hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
          {t("traders.port.col.amountWon")} <SortIcon active={sortKey === "amountWon"} dir={sortDir} />
        </button>
      </div>

      <div className="space-y-1">
        {shown.map((pos, i) => (
          <ClosedPositionRow key={`${pos.asset}-${i}`} position={pos} />
        ))}
      </div>

      {visible < positions.length && (
        <button
          onClick={onShowMore}
          className="w-full mt-4 py-2.5 text-sm font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 flex items-center justify-center gap-1 transition-colors"
        >
          <ChevronDown className="w-4 h-4" />
          {t("traders.port.showMore", { n: positions.length - visible })}
        </button>
      )}
    </>
  );
}

function ClosedPositionRow({ position: pos, source = 'polymarket' }: { position: ClosedPosition; source?: string }) {
  const t = useT();
  const isYes = pos.outcome?.toLowerCase() === "yes" || pos.outcomeIndex === 0;
  const pnl = pos.realizedPnl;
  // totalBought = shares count, cost = shares * avgPrice
  const cost = pos.totalBought * pos.avgPrice;
  const amountWon = cost + pnl;
  const pnlPct = cost > 0 ? (pnl / cost) * 100 : 0;
  const won = pnl > 0;

  return (
    <div className="flex items-center gap-2 md:grid md:grid-cols-12 md:gap-3 p-2.5 md:p-3 rounded-[5px] hover:bg-gray-50 dark:hover:bg-[#0f0f12] transition-colors">
      {/* Result badge — Polymarket style */}
      <div className="hidden md:flex md:col-span-1 items-center gap-1.5">
        <span className={`flex items-center gap-1 text-xs font-semibold ${won ? "text-emerald-500" : "text-red-400"}`}>
          {won ? (
            <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="8" fill="currentColor" fillOpacity="0.15"/><path d="M5 8l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          ) : (
            <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="8" fill="currentColor" fillOpacity="0.15"/><path d="M5.5 5.5l5 5M10.5 5.5l-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          )}
          {won ? t("traders.port.result.won") : t("traders.port.result.lost")}
        </span>
      </div>

      {/* Market icon + info */}
      <div className="md:col-span-5 flex items-center gap-2.5 min-w-0 flex-1">
        {/* Mobile: Won/Lost badge */}
        <span className={`md:hidden flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-[5px] ${
          won ? "bg-emerald-500/10 text-emerald-500" : "bg-red-500/10 text-red-400"
        }`}>
          {won ? "W" : "L"}
        </span>
        {pos.icon ? (
          <CdnImage
            src={pos.icon}
            alt={pos.title}
            width={40}
            height={40}
            className="w-9 h-9 md:w-10 md:h-10 rounded-[5px] object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-9 h-9 md:w-10 md:h-10 rounded-[5px] bg-gray-200 dark:bg-gray-700 flex-shrink-0" />
        )}
        <div className="min-w-0">
          {pos.slug ? (
            <Link
              href={source === 'predict' ? `/predictfun/event/${pos.slug}` : marketPageUrl({ marketSlug: pos.slug, eventSlug: pos.eventSlug, outcome: pos.outcome, source: 'polymarket', isMultiOutcome: pos.isMultiOutcome })}
              className="text-xs md:text-sm font-medium text-gray-900 dark:text-white truncate block hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              {pos.title}
            </Link>
          ) : (
            <p className="text-xs md:text-sm font-medium text-gray-900 dark:text-white truncate">
              {pos.title}
            </p>
          )}
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="text-[10px] md:text-xs text-gray-400">
              {formatShares(pos.totalBought)} {pos.outcome} at {formatPrice(pos.avgPrice)}
            </span>
          </div>
        </div>
      </div>

      {/* Total Traded (cost) — desktop */}
      <div className="hidden md:block md:col-span-2 text-right">
        <span className="text-sm font-medium text-gray-900 dark:text-white">{formatUsd(cost)}</span>
      </div>

      {/* Amount Won + PnL — desktop (stacked like Polymarket) */}
      <div className="md:col-span-4 text-right flex-shrink-0">
        <div className="text-xs md:text-sm font-bold text-gray-900 dark:text-white">
          {formatUsd(amountWon)}
        </div>
        <div className={`text-[10px] md:text-xs font-medium ${pnlColor(pnl)}`}>
          {pnlSign(pnl)}{formatUsd(pnl)} ({pnlSign(pnlPct)}{Math.abs(pnlPct).toFixed(2)}%)
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Activity Timeline
// ---------------------------------------------------------------------------

function ActivityTimeline({
  activity,
  loading,
  visible,
  onShowMore,
}: {
  activity: ActivityItem[];
  loading: boolean;
  visible: number;
  onShowMore: () => void;
}) {
  const t = useT();
  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="animate-pulse flex items-center gap-3 p-3 rounded-[5px] bg-gray-50 dark:bg-[#0f0f12]">
            <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-[5px]" />
            <div className="flex-1">
              <div className="h-4 w-1/2 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
              <div className="h-3 w-3/4 bg-gray-200 dark:bg-gray-700 rounded" />
            </div>
            <div className="h-5 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (activity.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-gray-400">{t("traders.port.empty.activity")}</p>
      </div>
    );
  }

  const shown = activity.slice(0, visible);

  return (
    <>
      {/* Header row — desktop only */}
      <div className="hidden md:grid grid-cols-12 gap-3 text-[10px] uppercase tracking-wider text-gray-400 font-semibold pb-2 mb-1 border-b border-gray-100 dark:border-[#1A1A1A]">
        <div className="col-span-1">{t("traders.port.col.type")}</div>
        <div className="col-span-7">{t("traders.port.col.market")}</div>
        <div className="col-span-2 text-right">{t("traders.port.col.amount")}</div>
        <div className="col-span-2 text-right">{t("traders.port.col.time")}</div>
      </div>

      <div className="space-y-1">
        {shown.map((item, i) => (
          <ActivityRow key={`${item.transactionHash || item.timestamp}-${i}`} item={item} />
        ))}
      </div>

      {visible < activity.length && (
        <button
          onClick={onShowMore}
          className="w-full mt-4 py-2.5 text-sm font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 flex items-center justify-center gap-1 transition-colors"
        >
          <ChevronDown className="w-4 h-4" />
          {t("traders.port.showMore", { n: activity.length - visible })}
        </button>
      )}
    </>
  );
}

function ActivityRow({ item }: { item: ActivityItem }) {
  const t = useT();
  // REWARD-like rows have no market attached (referral payouts, AMM
  // yield, generic rewards) — they were rendering as "Unknown market"
  // before 2026-05-18 because the row template fell through to the
  // market-info branch and tried to display a slug/icon that doesn't
  // exist for these. Group them all so the no-market header path is
  // taken once and `getTypeConfig` styles the badge per subtype.
  const isReward =
    item.type === "REWARD" ||
    item.type === "REFERRAL_REWARD" ||
    item.type === "YIELD" ||
    item.type === "MAKER_REBATE";
  const isRedeem = item.type === "REDEEM";
  const isTrade = item.type === "TRADE";
  const isBuy = item.side === "BUY";
  const isYes = item.outcome?.toLowerCase() === "yes" || item.outcomeIndex === 0;

  const typeConfig = getTypeConfig(item.type, item.side, t);
  const amount = item.usdcSize || (item.size * item.price) || 0;

  return (
    <div className="flex items-center gap-2 md:grid md:grid-cols-12 md:gap-3 p-2.5 md:p-3 rounded-[5px] hover:bg-gray-50 dark:hover:bg-[#0f0f12] transition-colors">
      {/* Type badge — text label like Polymarket */}
      <div className="md:col-span-1 flex-shrink-0">
        <span className={`inline-flex items-center justify-center px-2 py-1 rounded-[5px] text-[10px] font-bold uppercase tracking-wide ${typeConfig.badgeClass}`}>
          {typeConfig.label}
        </span>
      </div>

      {/* Market info */}
      <div className="md:col-span-7 flex items-center gap-2.5 min-w-0 flex-1">
        {isReward ? (
          <div className="min-w-0">
            <p className="text-xs md:text-sm font-medium text-gray-900 dark:text-white">
              {item.type === "REFERRAL_REWARD"
                ? t("traders.port.activity.referralReward")
                : item.type === "YIELD"
                  ? t("traders.port.activity.yield")
                  : (item.title || t("traders.port.activity.rewards4"))}
            </p>
            <span className="text-[10px] text-gray-400">
              {item.type === "REFERRAL_REWARD"
                ? t("traders.port.activity.referralRewardDesc")
                : item.type === "YIELD"
                  ? t("traders.port.activity.yieldDesc")
                  : t("traders.port.activity.rewards")}
            </span>
          </div>
        ) : (
          <>
            {item.icon ? (
              <CdnImage
                src={item.icon}
                alt={item.title || "Market"}
                width={36}
                height={36}
                className="w-8 h-8 md:w-9 md:h-9 rounded-[5px] object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-8 h-8 md:w-9 md:h-9 rounded-[5px] bg-gray-200 dark:bg-gray-700 flex-shrink-0" />
            )}
            <div className="min-w-0">
              {item.slug ? (
                <Link
                  href={marketPageUrl({ marketSlug: item.slug, eventSlug: item.eventSlug, outcome: item.outcome, source: 'polymarket', isMultiOutcome: item.isMultiOutcome })}
                  className="text-xs md:text-sm font-medium text-gray-900 dark:text-white truncate block hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  {item.title}
                </Link>
              ) : (
                <p className="text-xs md:text-sm font-medium text-gray-900 dark:text-white truncate">
                  {item.title || t("traders.port.activity.unknownMarket")}
                </p>
              )}
              {isTrade && (
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className={`text-[10px] font-bold ${isYes ? "text-emerald-500" : "text-red-400"}`}>
                    {item.outcome} {formatPrice(item.price)}
                  </span>
                  <span className="text-[10px] text-gray-400">
                    {t("traders.port.activity.sharesSuffix", { n: formatShares(item.size) })}
                  </span>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Amount */}
      <div className="md:col-span-2 text-right flex-shrink-0">
        <span className={`text-xs md:text-sm font-bold ${
          isReward ? "text-emerald-500" :
          (isTrade && !isBuy) ? "text-emerald-500" :
          "text-gray-900 dark:text-white"
        }`}>
          {isReward && "+"}
          {isTrade && !isBuy && "+"}
          {formatUsd(amount)}
        </span>
      </div>

      {/* Time */}
      <div className="md:col-span-2 text-right flex-shrink-0">
        <span className="text-[10px] md:text-xs text-gray-400">
          {timeAgo(new Date(item.timestamp * 1000))}
        </span>
      </div>
    </div>
  );
}

function getTypeConfig(type: string, side: string, t: (key: string, vars?: Record<string, string | number>) => string) {
  switch (type) {
    case "REWARD":
      return { label: t("traders.port.badge.reward"), badgeClass: "bg-pink-500/10 text-pink-500" };
    case "REFERRAL_REWARD":
      return { label: t("traders.port.badge.referral"), badgeClass: "bg-cyan-500/10 text-cyan-500" };
    case "YIELD":
      return { label: t("traders.port.badge.yield"), badgeClass: "bg-amber-500/10 text-amber-500" };
    case "REDEEM":
      return { label: t("traders.port.badge.redeem"), badgeClass: "bg-emerald-500/10 text-emerald-500" };
    case "TRADE":
      return side === "BUY"
        ? { label: t("traders.port.badge.buy"), badgeClass: "bg-blue-500/10 text-blue-500" }
        : { label: t("traders.port.badge.sell"), badgeClass: "bg-orange-500/10 text-orange-500" };
    case "SPLIT":
      return { label: t("traders.port.badge.split"), badgeClass: "bg-purple-500/10 text-purple-500" };
    case "MERGE":
      return { label: t("traders.port.badge.merge"), badgeClass: "bg-purple-500/10 text-purple-500" };
    case "CONVERSION":
      return { label: t("traders.port.badge.convert"), badgeClass: "bg-gray-500/10 text-gray-400" };
    case "MAKER_REBATE":
      return { label: t("traders.port.badge.rebate"), badgeClass: "bg-teal-500/10 text-teal-500" };
    default:
      return { label: type, badgeClass: "bg-gray-500/10 text-gray-400" };
  }
}

// ---------------------------------------------------------------------------
// Comments Timeline
// ---------------------------------------------------------------------------

function CommentsTimeline({
  comments,
  loading,
  visible,
  onShowMore,
}: {
  comments: TraderComment[];
  loading: boolean;
  visible: number;
  onShowMore: () => void;
}) {
  const t = useT();
  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="animate-pulse p-4 rounded-[5px] bg-gray-50 dark:bg-[#0f0f12]">
            <div className="h-3 w-1/3 bg-gray-200 dark:bg-gray-700 rounded mb-3" />
            <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded mb-2" />
            <div className="h-4 w-2/3 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (comments.length === 0) {
    return (
      <div className="text-center py-12">
        <MessageSquare className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
        <p className="text-sm text-gray-400">{t("traders.port.empty.comments")}</p>
      </div>
    );
  }

  const shown = comments.slice(0, visible);

  return (
    <>
      <div className="space-y-2">
        {shown.map((comment) => (
          <CommentRow key={comment.id} comment={comment} />
        ))}
      </div>

      {visible < comments.length && (
        <button
          onClick={onShowMore}
          className="w-full mt-4 py-2.5 text-sm font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 flex items-center justify-center gap-1 transition-colors"
        >
          <ChevronDown className="w-4 h-4" />
          {t("traders.port.showMore", { n: comments.length - visible })}
        </button>
      )}
    </>
  );
}

function CommentRow({ comment }: { comment: TraderComment }) {
  const marketLink = comment.eventSlug ? `/polymarket/event/${comment.eventSlug}` : null;

  return (
    <div className="p-3 md:p-4 rounded-[5px] hover:bg-gray-50 dark:hover:bg-[#0f0f12] transition-colors">
      {/* Market context */}
      {comment.eventTitle && (
        <div className="flex items-center gap-2 mb-2">
          {comment.eventImage && (
            <CdnImage
              src={comment.eventImage}
              alt={comment.eventTitle}
              width={20}
              height={20}
              className="w-5 h-5 rounded flex-shrink-0"
            />
          )}
          {marketLink ? (
            <Link
              href={marketLink}
              className="text-[11px] font-medium text-gray-500 dark:text-gray-400 truncate hover:text-gray-700 dark:hover:text-gray-300 transition-colors flex items-center gap-1"
            >
              {comment.eventTitle}
              <ExternalLink className="w-3 h-3 flex-shrink-0" />
            </Link>
          ) : (
            <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400 truncate">
              {comment.eventTitle}
            </span>
          )}
        </div>
      )}

      {/* Comment body */}
      <p className="text-sm text-gray-900 dark:text-white leading-relaxed whitespace-pre-line break-words">
        {comment.body}
      </p>

      {/* Footer: reactions + time */}
      <div className="flex items-center gap-3 mt-2">
        {comment.reactionCount > 0 && (
          <span className="flex items-center gap-1 text-[11px] text-gray-400">
            <ThumbsUp className="w-3 h-3" />
            {comment.reactionCount}
          </span>
        )}
        <span className="text-[11px] text-gray-400">
          {timeAgo(new Date(comment.createdAt))}
        </span>
      </div>
    </div>
  );
}
